import { getDataForReportRun, scheduleReport } from "@/Api/ReportApi";
import { GetDataForReportRunInputSchema } from "@/Schemas/Reports/GetDataForReportRunInput";
import { GetDataForReportRunOutput } from "@/Schemas/Reports/GetDataForReportRunOutput";
import { Box, Button, CircularProgress, Divider, Typography, useTheme, TextField, FormGroup, Tooltip, IconButton, Autocomplete, TextFieldProps, FormControlLabel, Checkbox, Alert } from "@mui/material"
import { useEffect, useRef, useState } from "react";
import { IoCubeOutline } from "react-icons/io5";
import {
    getCenterStyles, getYCenterStyles, getFluidBoxStyles, useTask,
    PopupBody, PopupFooter, PopupProvider, getCenteredSpaceBetweenStyles,
    SnackbarProvider,
    SnackbarType,
    UseTaskReturn,
} from "vig-ui-library";
import { DataNotFound } from "../DataNotFound";
import { ReportParameterExecuteDto } from "@/Schemas/Reports/ReportParameterExecuteDto";
import { ReportParameterTypeEnum } from "@/Enums/ReportParameterType";
import { IoIosPlayCircle, IoMdInformationCircle } from "react-icons/io";
import { DatePicker } from "@mui/x-date-pickers";
import { AssemblyDescription } from "./AssemblyDescription";
import { ReportExecutionHistoryParameterDto } from "@/Schemas/Reports/ReportExecutionHistoryParameterDto";
import { ReportExecutionHistoryParameterValueDto } from "@/Schemas/Reports/ReportExecutionHistoryParameterValueDto";
import { ParameterListValueTypeEnum } from "@/Enums/ParameterListValueType";
import dayjs, { Dayjs } from "dayjs";
import { ScheduleReportInput, ScheduleReportInputSchema } from "@/Schemas/Reports/ScheduleReportInput";
import { LoadingButton } from "@mui/lab";
import { GetMyReportsExecutionHistoryOutput } from "@/Schemas/Reports/GetMyReportsExecutionHistoryOutput";

export type RunAssemblyProps = {
    id: number;
    popupId: string;
    refreshExecutionHistoryTask: UseTaskReturn<[], GetMyReportsExecutionHistoryOutput>;
}

export const RunAssembly = (props: RunAssemblyProps) => {
    const theme = useTheme();

    const { openSnackbar } = SnackbarProvider.useSnackbar();

    const { closePopup } = PopupProvider.usePopups();

    const [sendNotification, setSendNotification] = useState(false);

    const [isRunningPeriodically, setIsRunningPeriodically] = useState(false);

    const [error, setError] = useState(false);

    const [requiredAlertVisible, setRequiredAlertVisible] = useState(false);

    /// Using useRef instead of useState to prevent re-renders everytime,
    /// when any parameter value changes in the form. It's not necessary to re-render
    /// because other components are not dependent on this state.
    const inputParametersRef = useRef<Omit<ReportExecutionHistoryParameterDto, "reportParameter">[]>([]);

    const updateParameter = (changedParameter: Omit<ReportExecutionHistoryParameterDto, "reportParameter">) => {
        const { current: parameters } = inputParametersRef;
        const exists = parameters.some((p) => p.reportParameterId === changedParameter.reportParameterId);

        if (!exists) {
            inputParametersRef.current = [...parameters, changedParameter];
            return;
        }

        const updatedParameters = parameters.map((parameter) => {
            if (parameter.reportParameterId === changedParameter.reportParameterId) return changedParameter;
            return parameter;
        });

        inputParametersRef.current = updatedParameters;
    }

    const { perform, value, isRunning, } = useTask<[], GetDataForReportRunOutput | null>(async () => {
        try {
            const input = GetDataForReportRunInputSchema.parse({ reportId: props.id });
            const response = await getDataForReportRun(input);
            return response;
        } catch (err) {
            setError(true);
            return null;
        }
    }, { drop: true });

    useEffect(() => {
        if (!value) perform();
    }, []);

    const validateParameters = (): boolean => {
        const requiredParametersIds = (value?.reportToRun.parameters || [])
            .filter((p) => !p.isOptional)
            .map((p) => p.reportParameterId);

        let isValid = true;

        for (const parameterId of requiredParametersIds) {
            const foundInputParameter = inputParametersRef.current.find((p) => p.reportParameterId === parameterId);
            if (!foundInputParameter) {
                isValid = false;
                break;
            }
            const hasValue = foundInputParameter.values.some((v) => !!v.value);
            if (!hasValue) {
                isValid = false;
                break;
            }
        }

        return isValid;
    }

    const scheduleReportTask = useTask(async () => {
        try {
            const isValid = validateParameters();

            if (!isValid) {
                setRequiredAlertVisible(true);
                return;
            }

            setRequiredAlertVisible(false);

            const input: ScheduleReportInput = ScheduleReportInputSchema.parse({
                reportId: props.id,
                sendNotification,
                reportUrl: window.location.href,
                parameters: inputParametersRef.current,
                isRunningPeriodically,
            });

            await scheduleReport(input);

            props.refreshExecutionHistoryTask.perform();

            openSnackbar({
                text: "Zostava bola úspešne spustená",
                type: SnackbarType.SUCCESS,
            });

            closePopup(props.popupId);
        } catch (err) {
            openSnackbar({
                text: "Nepodarilo sa spustiť zostavu, skúste to znova. Ak problém pretrváva, kontaktujte IT.",
                type: SnackbarType.ERROR,
            });
        }
    });

    if (isRunning) return (
        <Box sx={{ ...getFluidBoxStyles(), ...getCenterStyles(), padding: 2, }}>
            <CircularProgress />
        </Box>
    );

    if (error) return <Box sx={{ ...getFluidBoxStyles(), ...getCenterStyles("column"), padding: 2, gap: 1 }}>
        <IoCubeOutline size={32} color={theme.palette.error.main} />
        <Typography variant="h6">Nepodarilo sa načítať dáta</Typography>
        <Typography variant="body2">
            Skúste to znova, ak problém pretrváva, kontaktujte IT.
        </Typography>
        <Button onClick={perform} sx={{ mt: 1 }} color="error">
            Skúsiť znova
        </Button>
    </Box>

    if (!value) return <DataNotFound
        title="Žiadne dáta sa nenašli"
        description="Skúste to znova, ak problém pretrváva, kontaktujte IT."
    />

    const { expiresInDays, parameters, isPeriodicReport } = value.reportToRun;

    return (
        <>
            <PopupBody>
                <Box sx={{ padding: 2, gap: 2, display: "flex", flexDirection: "column", paddingBottom: requiredAlertVisible ? "80px" : 4 }}>
                    {!!expiresInDays && (
                        <Box>
                            <Typography variant="body1">Expirácia</Typography>

                            <Typography variant="subtitle1" sx={{ whiteSpace: "pre-line" }}>
                                Zostava bude expirovať o {expiresInDays} dní po dokončení
                            </Typography>
                        </Box>
                    )}

                    <AssemblyDescription description={value.reportToRun.description} />

                    <Divider />

                    <Box>
                        <Typography variant="h6" marginBottom={2}>Parametre</Typography>

                        {!parameters.length && (
                            <Typography variant="subtitle1" sx={{ whiteSpace: "pre-line" }}>
                                Táto zostava nemá žiadne parametre
                            </Typography>
                        )}

                        {!!parameters.length && <ParametersFields
                            parameters={parameters}
                            isPeriodicReport={isPeriodicReport}
                            isRunningPeriodically={isRunningPeriodically}
                            setIsRunningPeriodically={setIsRunningPeriodically}
                            inputParametersRef={inputParametersRef}
                            updateParameter={updateParameter}
                        />}
                    </Box>
                </Box>
            </PopupBody>

            {requiredAlertVisible && (
                <Box sx={{
                    position: "absolute",
                    bottom: 80,
                    paddingX: 2,
                    width: "100%",
                    zIndex: 2
                }}>
                    <Alert severity="error" onClose={() => setRequiredAlertVisible(false)}>
                        Prosím vyplňte všetky povinné parametre.
                    </Alert>
                </Box>
            )}

            <PopupFooter>
                <Box sx={{ ...getCenteredSpaceBetweenStyles(), flexWrap: "wrap", gap: 1 }}>
                    <Box sx={{ ...getYCenterStyles() }}>
                        <FormControlLabel control={<Checkbox
                            checked={sendNotification}
                            onChange={(e) => setSendNotification(e.target.checked)}
                        />} label="Prajem si notifikácie ohľadom zmeny stavu zostavy" />
                    </Box>

                    <Box sx={{ ...getYCenterStyles(), gap: 1 }}>
                        <LoadingButton
                            loading={scheduleReportTask.isRunning}
                            size="medium"
                            sx={{ whiteSpace: "nowrap" }}
                            endIcon={<IoIosPlayCircle size={24} />}
                            onClick={() => scheduleReportTask.perform()}
                        >
                            Spustiť zostavu
                        </LoadingButton>

                        <Button size="medium" color="secondary" onClick={(e) => {
                            e.stopPropagation();
                            closePopup(props.popupId);
                        }}>
                            Zatvoriť
                        </Button>
                    </Box>
                </Box>
            </PopupFooter>
        </>
    );
}

type ParamatersFieldsProps = {
    parameters: ReportParameterExecuteDto[];
    inputParametersRef: React.MutableRefObject<Omit<ReportExecutionHistoryParameterDto, "reportParameter">[]>;
    updateParameter: (changedParameter: Omit<ReportExecutionHistoryParameterDto, "reportParameter">) => void;
    isPeriodicReport: boolean;
    isRunningPeriodically: boolean;
    setIsRunningPeriodically: React.Dispatch<React.SetStateAction<boolean>>;
}

const ParametersFields = (props: ParamatersFieldsProps) => {
    const { updateParameter, } = props;

    const getField = (parameter: ReportParameterExecuteDto) => {
        const inputParameter = props.inputParametersRef.current.find((p) => p.reportParameterId === parameter.reportParameterId);

        const fieldProps = { inputParameter, parameter, updateParameter };

        switch (parameter.type) {
            case ReportParameterTypeEnum.TEXT:
                return <ParameterTextField {...fieldProps} />;
            case ReportParameterTypeEnum.NUMBER:
                return <ParameterNumberField {...fieldProps} />;
            case ReportParameterTypeEnum.DATE:
                return <ParameterDateField {...fieldProps} />;
            case ReportParameterTypeEnum.LIST:
                return <ParameterListField {...fieldProps} />;
            default:
                return <></>
        }
    };

    return <FormGroup sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {props.parameters.map((parameter) => {
            return <Box key={parameter.reportParameterId} sx={{ ...getYCenterStyles(), width: "100%" }}>
                {getField(parameter)}

                {parameter.description && (
                    <Tooltip title={parameter.description}>
                        <IconButton>
                            <IoMdInformationCircle size="24" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        })}

        {/* 
            We decided to disable this feature for this application due to an automatic scheduling issue. 
            For now, there are no periodic request assemblies, so it's not necessary to implement this feature.   
        */}
        {/* {props.isPeriodicReport && (
            <Box>
                <FormControlLabel
                    control={<Switch checked={isRunningPeriodically} onChange={() => {
                        setIsRunningPeriodically((prev) => !prev);
                    }} />}
                    label="Periodická zostava"
                />

                {isRunningPeriodically && (
                    <Autocomplete
                        sx={{ width: "100%", marginTop: 1 }}
                        disablePortal
                        value="Denne"
                        options={["Denne"]}
                        renderInput={(inheritedProps) => <TextField
                            {...inheritedProps}
                            size="small"
                            variant="filled"
                            label="Periodicita"
                            placeholder="Vyberte periodicitu"
                            fullWidth
                        />}
                    />
                )}
            </Box>
        )} */}
    </FormGroup>
};

type FieldProps = {
    parameter: ReportParameterExecuteDto;
    inputParameter?: Omit<ReportExecutionHistoryParameterDto, "reportParameter">;
    updateParameter: (changedParameter: Omit<ReportExecutionHistoryParameterDto, "reportParameter">) => void;
}

const getSharedFieldProps = (parameter: ReportParameterExecuteDto): Partial<TextFieldProps> => ({
    fullWidth: true,
    label: parameter.name,
    placeholder: parameter.name || "",
    size: "small",
    variant: "filled",
    required: !parameter.isOptional,
})

const ParameterTextField = ({ parameter, updateParameter }: FieldProps) => {
    const getParameterValue = (): string => {
        if (!parameter) return "";
        if (!parameter.values?.length) return "";
        return parameter.values[0].value?.toString() || "";
    }

    const [localeValue, setLocaleValue] = useState<string>(getParameterValue());

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value

        const value: ReportExecutionHistoryParameterValueDto = {
            value: newValue,
            listValueType: ParameterListValueTypeEnum.TEXT,
        }

        const changedParameter = { reportParameterId: parameter.reportParameterId, values: [value] };

        updateParameter(changedParameter);

        setLocaleValue(newValue);
    }

    return (
        <TextField
            {...getSharedFieldProps(parameter)}
            type="text"
            value={localeValue}
            onChange={onChange}
        />
    );
}

const ParameterNumberField = ({ parameter, updateParameter }: FieldProps) => {
    const parseNumber = (value: string | number | null): number | null => {
        if (!value) return null;
        if (typeof value === "number") return value;
        const number = parseFloat(value);
        if (isNaN(number)) return null;
        return number;
    }

    const getParameterValue = (): number | null => {
        if (!parameter) return null;
        if (!parameter.values?.length) return null;
        return parseNumber(parameter.values[0].value || null);
    }

    const [localeValue, setLocaleValue] = useState<number | null>(getParameterValue());

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value

        const value: ReportExecutionHistoryParameterValueDto = {
            value: parseNumber(newValue),
            listValueType: ParameterListValueTypeEnum.NUMBER,
        }

        const changedParameter = { reportParameterId: parameter.reportParameterId, values: [value] };

        updateParameter(changedParameter);

        setLocaleValue(parseNumber(newValue));
    }

    return (
        <TextField
            {...getSharedFieldProps(parameter)}
            type="number"
            value={localeValue ? localeValue.toString() : ""}
            onChange={onChange}
        />
    );
}

const ParameterDateField = ({ parameter, updateParameter }: FieldProps) => {
    const getParameterValue = (): Dayjs | null => {
        if (!parameter) return null;
        if (!parameter.values?.length) return null;
        const value = parameter.values[0].value;
        if (!value) return null;
        return dayjs(value);
    }

    const [localeValue, setLocaleValue] = useState<Dayjs | null>(getParameterValue());

    const onChange = (newValue: Dayjs | null) => {
        const value: ReportExecutionHistoryParameterValueDto = {
            value: newValue ? newValue.toISOString() : null,
            listValueType: ParameterListValueTypeEnum.TEXT,
        }

        const changedParameter = { reportParameterId: parameter.reportParameterId, values: [value] };

        updateParameter(changedParameter);

        setLocaleValue(newValue);
    }

    return (
        <DatePicker
            label={"Vyberte obdobie"}
            views={["year", "month", "day"]}
            value={localeValue}
            onChange={onChange}
            // render field
            slots={{
                textField: (inheritedProps) => <TextField
                    {...inheritedProps}
                    {...getSharedFieldProps(parameter)}
                />,
            }}
        />
    );
}

type SelectOption = {
    label: string | null;
    value: string | number | null;
    type: ParameterListValueTypeEnum | null;
}

const ParameterListField = (props: FieldProps) => {
    const getOptions = (): SelectOption[] => {
        if (!props.parameter.selectableValues) return [];

        return props.parameter.selectableValues.map((option) => ({
            label: option.name || option.value,
            value: option.value,
            type: option.type,
        }));
    }

    const options = getOptions();

    if (props.parameter.allowMultiselect) return <ParameterMultiSelectField {...props} options={options} />;

    return <ParameterSelectField {...props} options={options} />;
}

const ParameterSelectField = ({ parameter, options, updateParameter }: FieldProps & { options: SelectOption[] }) => {
    const getParameterValue = (): SelectOption | null => {
        if (!parameter || !parameter.values?.length) return null;
        const value = parameter.values[0].value;
        if (!value) return null;
        const option = options.find((o) => o.value === value);
        return option || null;
    }

    const [localeValue, setLocaleValue] = useState<SelectOption | null>(getParameterValue());

    const onChange = (selectedOption: SelectOption | null) => {
        const value: ReportExecutionHistoryParameterValueDto = {
            value: selectedOption?.value?.toString() || null,
            listValueType: selectedOption?.type || ParameterListValueTypeEnum.NUMBER,
        }

        const changedParameter = { reportParameterId: parameter.reportParameterId, values: [value] };

        updateParameter(changedParameter);

        setLocaleValue(selectedOption);
    }

    return (
        <Autocomplete
            sx={{ width: "100%" }}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            options={options}
            value={localeValue}
            onChange={(_e, newValue) => onChange(newValue)}
            renderInput={(inheritedProps) => <TextField
                {...inheritedProps}
                {...getSharedFieldProps(parameter)}
            />}
        />
    );
}

const ParameterMultiSelectField = ({ parameter, options, updateParameter }: FieldProps & { options: SelectOption[] }) => {
    const getParameterValues = (): SelectOption[] => {
        if (!parameter || !parameter.values?.length) return [];

        const values = parameter.values.map((value) => {
            const option = options.find((o) => o.value === value.value);
            return option || null;
        });

        return values.filter((v) => !!v) as SelectOption[];
    }

    const [localeValues, setLocaleValues] = useState<SelectOption[]>(getParameterValues());

    const onChange = (selectedOptions: SelectOption[]) => {
        const values = selectedOptions.map((option) => ({
            value: option.value?.toString() || null,
            listValueType: option.type || ParameterListValueTypeEnum.NUMBER,
        }));

        const changedParameter = { reportParameterId: parameter.reportParameterId, values };

        updateParameter(changedParameter);

        setLocaleValues(selectedOptions);
    }

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            sx={{ width: "100%" }}
            options={options}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            value={localeValues}
            onChange={(_e, newValue) => onChange(newValue)}
            renderInput={(inheritedProps) => <TextField
                {...inheritedProps}
                {...getSharedFieldProps(parameter)}
            />}
        />
    );
}